import { useState, type ReactNode } from 'react';
import { IconChevronLeft, IconChevronRight } from '@/lib/icons';
import { useReducedMotion } from '@/hooks/useReducedMotion';

export interface SimulationFrame {
  frame: ReactNode;
  label: string;
  // duration is retained so authored step arrays stay valid; stepping is now
  // manual, so it is no longer used to auto-advance.
  duration?: number;
}

interface Props {
  steps: SimulationFrame[];
  className?: string;
}

// UISimulation walks through a list of pre-authored frames. Stepping is manual
// — the viewer clicks Prev/Next — so nothing animates on its own. Every frame
// is stacked into the same grid cell, so the figure is always as tall as the
// tallest frame and stepping never shifts the surrounding article. When the OS
// prefers reduced motion, the final frame is shown statically with no controls.
export function UISimulation({ steps, className }: Props): JSX.Element {
  const reduced = useReducedMotion();
  const [index, setIndex] = useState(0);

  const total = steps.length;
  if (total === 0) return <div className={className ?? ''} />;

  const containerCls =
    'rounded-lg border border-dash-border bg-dash-surface/40 p-4 ' + (className ?? '');

  if (reduced) {
    const last = steps[total - 1];
    return (
      <figure className={containerCls}>
        <div className="relative min-h-[160px]">{last?.frame}</div>
        <figcaption className="mt-3 flex items-center gap-2 text-xs text-dash-secondary">
          <span className="font-mono text-dash-muted">
            {total} / {total}
          </span>
          <span>{last?.label}</span>
        </figcaption>
      </figure>
    );
  }

  const safeIndex = Math.min(index, total - 1);
  const current = steps[safeIndex];
  const atStart = safeIndex === 0;
  const atEnd = safeIndex === total - 1;

  const navBtn =
    'p-1 rounded text-dash-secondary hover:text-dash-primary hover:bg-dash-border/40 ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 ' +
    'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent ' +
    'disabled:hover:text-dash-secondary';

  return (
    <figure className={containerCls}>
      {/* Every frame shares grid cell 1/1, so the row is sized to the tallest
          frame and the figure height is constant across steps. */}
      <div className="relative grid min-h-[160px]">
        {steps.map((s, i) => (
          <div
            key={i}
            style={{ gridArea: '1 / 1' }}
            aria-hidden={i !== safeIndex}
            className={
              'transition-opacity duration-200 ' +
              (i === safeIndex ? 'opacity-100' : 'opacity-0 pointer-events-none')
            }
          >
            {s.frame}
          </div>
        ))}
      </div>
      <figcaption className="mt-3 flex items-center gap-3 text-xs text-dash-secondary">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            aria-label="Previous step"
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
            disabled={atStart}
            className={navBtn}
          >
            <IconChevronLeft size={16} />
          </button>
          <span className="font-mono text-dash-muted tabular-nums">
            {safeIndex + 1} / {total}
          </span>
          <button
            type="button"
            aria-label="Next step"
            onClick={() => setIndex((i) => Math.min(total - 1, i + 1))}
            disabled={atEnd}
            className={navBtn}
          >
            <IconChevronRight size={16} />
          </button>
        </div>
        <span className="flex-1 truncate">{current?.label}</span>
      </figcaption>
    </figure>
  );
}
